import { useEffect, useState } from 'react';

import { COREBASEURL } from '../constants/global';
import { type IPartyCustomization } from '../interface/ICustomization';

// Some backends serialise absent values as the literal strings "null" /
// "undefined". Coerce those (and blanks) to a real null so downstream code can
// rely on `field ?? fallback`.
const coerceString = (value: unknown): string | null => {
	if (value === null || value === undefined) return null;
	if (typeof value !== 'string') return null;
	const trimmed = value.trim();
	if (trimmed === '' || trimmed.toLowerCase() === 'null' || trimmed.toLowerCase() === 'undefined') return null;
	return trimmed;
};

const coerceBool = (value: unknown): boolean | null => {
	if (value === null || value === undefined) return null;
	if (typeof value === 'boolean') return value;
	if (typeof value === 'string') {
		const trimmed = value.trim().toLowerCase();
		if (trimmed === 'true') return true;
		if (trimmed === 'false') return false;
	}
	return null;
};

const coerceNumber = (value: unknown): number | null => {
	if (value === null || value === undefined) return null;
	if (typeof value === 'number') return Number.isFinite(value) ? value : null;
	if (typeof value === 'string' && value.trim() !== '' && !Number.isNaN(Number(value))) return Number(value);
	return null;
};

const normaliseCustomization = (raw: Record<string, unknown>): IPartyCustomization => ({
	partyId: coerceNumber(raw.partyId),
	electionId: coerceNumber(raw.electionId),
	bannerImageUrl: coerceString(raw.bannerImageUrl),
	backgroundImageUrl: coerceString(raw.backgroundImageUrl),
	logoUrl: coerceString(raw.logoUrl),
	candidatePhotoUrl: coerceString(raw.candidatePhotoUrl),
	videoUrl: coerceString(raw.videoUrl),
	primaryColor: coerceString(raw.primaryColor),
	secondaryColor: coerceString(raw.secondaryColor),
	accentColor: coerceString(raw.accentColor),
	headline: coerceString(raw.headline),
	subheadline: coerceString(raw.subheadline),
	welcomeMessage: coerceString(raw.welcomeMessage),
	callToActionText: coerceString(raw.callToActionText),
	websiteUrl: coerceString(raw.websiteUrl),
	facebookUrl: coerceString(raw.facebookUrl),
	twitterUrl: coerceString(raw.twitterUrl),
	instagramUrl: coerceString(raw.instagramUrl),
	youtubeUrl: coerceString(raw.youtubeUrl),
	whatsAppUrl: coerceString(raw.whatsAppUrl),
	showCandidatePhoto: coerceBool(raw.showCandidatePhoto),
	showVideo: coerceBool(raw.showVideo),
	showSocialLinks: coerceBool(raw.showSocialLinks),
});

export interface UsePartyCustomizationResult {
	data: IPartyCustomization | null;
	loading: boolean;
}

/**
 * Fetches the anonymous party-customization record for a `{partyGuid, electionGuid}`
 * pair. Never throws — a 404 / null / network failure resolves to `data: null`
 * so callers fall back to the default (un-branded) experience.
 */
export const usePartyCustomization = (partyGuid?: string | null, electionGuid?: string | null): UsePartyCustomizationResult => {
	const [data, setData] = useState<IPartyCustomization | null>(null);
	const [loading, setLoading] = useState<boolean>(false);

	useEffect(() => {
		if (!partyGuid) {
			setData(null);
			return;
		}

		let active = true;
		setLoading(true);

		const query = electionGuid ? `?election=${encodeURIComponent(electionGuid)}` : '';
		const url = `${COREBASEURL}Party/guid/${encodeURIComponent(partyGuid)}/customization${query}`;

		fetch(url, { method: 'GET', headers: { 'Content-type': 'application/json' } })
			.then(async (res) => (res.ok ? ((await res.json()) as Record<string, unknown>) : null))
			.then((json) => {
				if (!active) return;
				// Envelope keys are PascalCase (`Data`); tolerate a lowercase `data`
				// too in case the serializer casing changes.
				const payload = (json?.Data ?? json?.data ?? null) as Record<string, unknown> | null;
				setData(payload ? normaliseCustomization(payload) : null);
			})
			.catch(() => {
				if (active) setData(null);
			})
			.finally(() => {
				if (active) setLoading(false);
			});

		return () => {
			active = false;
		};
	}, [partyGuid, electionGuid]);

	return { data, loading };
};

export default usePartyCustomization;
